// ===================================================================
// HMGP — Module Layout
// Disaster selector bar drives all pages via shared store.
// ===================================================================

import { useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Group, Text, Select } from '../../../components/gab/GabComponents';
import {
  IconLayoutDashboard, IconClipboardList, IconFileDescription,
  IconBuildingCommunity, IconAlertTriangle, IconMap, IconReceipt,
  IconStars, IconCalendarStats, IconFileInvoice, IconSettings,
} from '@tabler/icons-react';
import { useFilteredNav } from '../../../hooks/useFilteredNav';
import { useRoleStore } from '../../../store/roleStore';
import { usePAGrantsStore } from '../../../store/paGrantsStore';
import { useActiveDisaster } from '../../../hooks/useActiveDisaster';
import type { NavItem } from '../../../types/nav';
import HMGPApplicantPortal from './HMGPApplicantPortal';
import PageHeader from '../../../components/ui/PageHeader';
import ModuleNav from '../../../components/layout/ModuleNav';

const APPLICANT_SLUGS = ['pa_applicant_mgr', 'pa_applicant_staff', 'pa_applicant_viewer'];

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard',       path: '/pagrants/hmgp',                      icon: IconLayoutDashboard, exact: true },
  { label: 'Subapplications', path: '/pagrants/hmgp/subapplications',      icon: IconFileDescription },
  { label: 'Projects',        path: '/pagrants/hmgp/projects',             icon: IconClipboardList },
  { label: 'Applicants',      path: '/pagrants/hmgp/applicants',           icon: IconBuildingCommunity },
  { label: 'Disasters',       path: '/pagrants/hmgp/disasters',            icon: IconAlertTriangle },
  { label: 'Map',             path: '/pagrants/hmgp/map',                  icon: IconMap },
  { label: 'Reimbursements',  path: '/pagrants/hmgp/reimbursements',       icon: IconReceipt },
  { label: 'FEMA Forms',      path: '/pagrants/hmgp/forms',                icon: IconFileInvoice,    minLevel: 'editor' },
  { label: 'Quarterly',       path: '/pagrants/hmgp/quarterly',            icon: IconCalendarStats },
  { label: 'Scoring',         path: '/pagrants/hmgp/scoring',              icon: IconStars,          minLevel: 'editor' },
  { label: 'Settings',        path: '/pagrants/hmgp/settings/checklist',   icon: IconSettings,       minLevel: 'admin' },
];

const NAV_GROUPS: { label: string; paths: string[] }[] = [
  { label: '', paths: ['/pagrants/hmgp', '/pagrants/hmgp/subapplications', '/pagrants/hmgp/projects', '/pagrants/hmgp/applicants', '/pagrants/hmgp/disasters', '/pagrants/hmgp/map', '/pagrants/hmgp/reimbursements'] },
  { label: 'Admin', paths: ['/pagrants/hmgp/forms', '/pagrants/hmgp/quarterly', '/pagrants/hmgp/scoring', '/pagrants/hmgp/settings/checklist'] },
];

export default function HMGPLayout() {
  const effectiveRole = useRoleStore(s => s.demoRoleOverride ?? s.myRole);
  const slug = effectiveRole?.roleSlug ?? '';
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const visibleItems = useFilteredNav('pagrants', NAV_ITEMS);

  // ── Disaster selector ──────────────────────────────────────────
  const disasters = usePAGrantsStore(s => s.disasters);
  const fetchDisasters = usePAGrantsStore(s => s.fetchDisasters);
  const activeDisasterId = usePAGrantsStore(s => s.activeDisasterId);
  const setActiveDisasterId = usePAGrantsStore(s => s.setActiveDisasterId);
  const autoDisaster = useActiveDisaster();

  useEffect(() => { fetchDisasters(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setActiveDisasterId(autoDisaster ? String(autoDisaster.Rid) : null);
  }, [autoDisaster?.Rid]); // eslint-disable-line react-hooks/exhaustive-deps

  const disasterOptions = disasters.map(d => ({
    value: String(d.Rid),
    label: [d.disasterNumber, d.disasterName].filter(Boolean).join(' — '),
  }));

  // ── Applicant portal fork ──────────────────────────────────────
  if (APPLICANT_SLUGS.includes(slug)) {
    return (
      <div style={{ padding: '24px' }}>
        <HMGPApplicantPortal />
      </div>
    );
  }

  const groups = NAV_GROUPS.map(g => ({
    ...g,
    items: g.paths.map(p => visibleItems.find(vi => vi.path === p)).filter(Boolean) as typeof visibleItems,
  })).filter(g => g.items.length > 0);

  const disasterSelector = (
    <Group gap="xs" align="center">
      <Text size="xs" fw={600} c="dimmed" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Disaster
      </Text>
      <Select
        size="xs"
        placeholder="Select a disaster…"
        data={disasterOptions}
        value={activeDisasterId}
        onChange={v => setActiveDisasterId(v)}
        style={{ width: 260 }}
        clearable
      />
    </Group>
  );

  return (
    <div style={{ padding: '24px' }}>
      <PageHeader
        breadcrumbs={[{ label: 'Recover' }, { label: 'Grants', route: '/pagrants' }, { label: 'HMGP' }]}
        title="HMGP"
        subtitle="Hazard Mitigation Grant Program — subapplications, projects and reimbursements"
      />
      <ModuleNav items={visibleItems} groups={groups} extra={disasterSelector} />
      <Outlet />
    </div>
  );
}
